import { MoneySchema } from "@redotech/money";
import type {
  ProductVariant,
  Customer as ShopifyCustomer,
} from "@redotech/redo-common-model/common/shopify-pixel-types";
import {
  CheckoutConversionMetricInformationStatus,
  CheckoutConversionMetricSource,
} from "@redotech/redo-conversion-metrics-common/checkout-conversion-metrics";
import { Provider } from "@redotech/redo-order-common/provider";
import { shopifyCheckoutSchema } from "@redotech/redo-shopify-checkout-schema-common/shopify-checkout";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  EventProducer,
  IdentityResolver,
} from "../shopify-storefront-event-payload";
import {
  baseCustomerEventSchema,
  CustomerEventType,
  PaymentFailureStage,
} from "./base";
import { coverageTreatmentIdSourceSchema } from "./coverage-treatment-id-source";
import {
  redoCartLineSchema,
  redoCheckoutInfoSchema,
  redoStorefrontEventProductSchema,
  shopifyCartLineSchema,
  shopifyCartLineToRedoCartLine,
  shopifyCheckoutToRedoCheckout,
  shopifyPixelEventCartSchema,
  shopifyProductVariantToRedoProductVariant,
} from "./storefront-event-primitives";

type PartialShopifyCustomer = Partial<ShopifyCustomer> | null | undefined;

export const LocalDayOfWeek = [
  "sun",
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
] as const;
export type LocalDayOfWeek = (typeof LocalDayOfWeek)[number];

export const baseShoppingEventSchema = baseCustomerEventSchema.extend({
  // Null for providers without an external event id (e.g. BigCommerce); those
  // set an explicit idempotencyKey instead.
  externalEventId: z.string().nullable(),
  externalCustomer: z
    .any()
    .optional()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    .transform((val: any) => val as PartialShopifyCustomer),
  sessionId: z.string().optional(),
  ipAddress: z.string().optional(),
  storefrontProvider: createEnum(Provider, { enumName: "Provider" }),
  /** @deprecated Read promoted top-level fields instead. This field will be removed after backfilling top level fields */
  rawEvent: z.any().nullish(),
  pricingTestTreatmentId: z.string().optional(),
  /** Coverage A/B treatment the shopper was assigned; set together with its source. */
  coverageTreatmentId: z.string().optional(),
  coverageTreatmentIdSource: coverageTreatmentIdSourceSchema.optional(),
  /** How the email was resolved. Pixel-only; non-shopping events don't carry this. */
  emailIdentityResolver: createEnum(IdentityResolver, {
    enumName: "IdentityResolver",
  }).optional(),
  /** How the phone was resolved. */
  phoneIdentityResolver: createEnum(IdentityResolver, {
    enumName: "IdentityResolver",
  }).optional(),
  /** Which system provided the email on this event. Matches producer for
   *  single-source events; differs when the checkout buffer merges redo
   *  base + contextual identity enrichment. */
  emailIdentityProducer: createEnum(EventProducer, {
    enumName: "EventProducer",
  }).optional(),
  /** Which system provided the phone on this event. */
  phoneIdentityProducer: createEnum(EventProducer, {
    enumName: "EventProducer",
  }).optional(),
});
export type BaseShoppingEvent = z.infer<typeof baseShoppingEventSchema>;

export const StorePageViewedShoppingEventSchema_ =
  baseShoppingEventSchema.extend({
    eventType: z.literal(CustomerEventType.STORE_PAGE_VIEWED),
    urlWithoutParams: zExt.coerceNull(z.string()),
    urlWithParams: zExt.coerceNull(z.string()),
  });
export const StorePageViewedShoppingEventSchema: z.ZodType<StorePageViewedShoppingEvent> =
  StorePageViewedShoppingEventSchema_;
export interface StorePageViewedShoppingEvent extends z.infer<
  typeof StorePageViewedShoppingEventSchema_
> {}

//////////////////////////////////////
/////////// PRODUCT VIEWED ///////////
//////////////////////////////////////
const baseProductViewedShoppingEventSchema = baseShoppingEventSchema.extend({
  eventType: z.literal(CustomerEventType.PRODUCT_VIEWED),
  viewedProduct: zExt.coerceNull(redoStorefrontEventProductSchema),
});

const shopifyProductViewedShoppingEventSchema =
  baseProductViewedShoppingEventSchema.extend({
    storefrontProvider: z
      .literal(Provider.SHOPIFY)
      .nullish()
      .transform((val) => val ?? Provider.SHOPIFY),
    /** @deprecated This field is shopify-specific. Use `viewedProduct` instead. */
    productVariant: z
      .any()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      .transform((val: any) => val as ProductVariant)
      .nullish(),
    /** @deprecated This field is shopify-specific. Use `cartState` instead. */
    cart: z.object({ id: z.string().nullish() }).nullish(), // intentionally stripped down since cart data is less useful for this event
  });
const commentsoldProductViewedShoppingEventSchema =
  baseProductViewedShoppingEventSchema.extend({
    storefrontProvider: z.literal(Provider.COMMENTSOLD),
  });
const bigcommerceProductViewedShoppingEventSchema =
  baseProductViewedShoppingEventSchema.extend({
    storefrontProvider: z.literal(Provider.BIGCOMMERCE),
  });
const commerceCloudProductViewedShoppingEventSchema =
  baseProductViewedShoppingEventSchema.extend({
    storefrontProvider: z.literal(Provider.COMMERCE_CLOUD),
  });
export const ProductViewedShoppingEventSchema_ = z.discriminatedUnion(
  "storefrontProvider",
  [
    shopifyProductViewedShoppingEventSchema,
    commentsoldProductViewedShoppingEventSchema,
    bigcommerceProductViewedShoppingEventSchema,
    commerceCloudProductViewedShoppingEventSchema,
  ],
);
export const ProductViewedShoppingEventSchema: z.ZodType<ProductViewedShoppingEvent> =
  ProductViewedShoppingEventSchema_;
export type ProductViewedShoppingEvent = z.infer<
  typeof ProductViewedShoppingEventSchema_
>;

/**
 * Translate from legacy storefront event schema to new provider-agnostic schema.
 *
 * Historically, our internal schema for these events was very Shopify-specific.
 * Now that we're going to be ingesting from Commentsold as well, we want to have
 * a more generic schema. Unless/until we migrate all of our events (a >1 week
 * migration), we need to handle events matching either schema.
 *
 * Can't include this transformation in the zod schema because then we couldn't put
 * the resulting schema in a discriminated union.
 */
export function standardizeProductViewedShoppingEvent(
  event: ProductViewedShoppingEvent,
  redoCoverageProductIds?: ReadonlySet<string>,
): ProductViewedShoppingEvent {
  if ("productVariant" in event) {
    if (!event.productVariant) {
      throw new Error("Malformed product viewed shopping event");
    }
    const { productVariant, cart, ...rest } = event;
    return {
      ...rest,
      viewedProduct: shopifyProductVariantToRedoProductVariant(
        productVariant,
        redoCoverageProductIds,
      ),
      rawEvent: event.rawEvent ?? { productVariant, cart },
    };
  }
  return event;
}

//////////////////////////////////////
///////// COLLECTION VIEWED //////////
//////////////////////////////////////
export const CollectionViewedShoppingEventSchema_ =
  baseShoppingEventSchema.extend({
    eventType: z.literal(CustomerEventType.COLLECTION_VIEWED),
    collection: zExt.coerceNull(
      z.object({
        id: zExt.coerceNull(z.string()),
        title: zExt.coerceNull(z.string()),
      }),
    ),
  });
export const CollectionViewedShoppingEventSchema: z.ZodType<CollectionViewedShoppingEvent> =
  CollectionViewedShoppingEventSchema_;
export interface CollectionViewedShoppingEvent extends z.infer<
  typeof CollectionViewedShoppingEventSchema_
> {}

//////////////////////////////////////
/////// PRODUCT ADDED TO CART ////////
//////////////////////////////////////
const baseCartEventSchema = baseShoppingEventSchema.extend({
  eventType: z.union([
    z.literal(CustomerEventType.PRODUCT_ADDED_TO_CART),
    z.literal(CustomerEventType.PRODUCT_REMOVED_FROM_CART),
  ]),
  cartState: zExt.coerceNull(
    z.object({
      subtotal: zExt.coerceNull(
        z.object({
          amount: zExt.coerceNull(z.number()),
          currencyCode: zExt.coerceNull(z.string()),
        }),
      ),
      cartId: zExt.coerceNull(z.string()),
      lineItems: zExt.coerceNull(z.array(redoCartLineSchema)),
    }),
  ),
  cartLineChanged: zExt.coerceNull(redoCartLineSchema),
});

export const ProductAddedToCartShoppingEventSchema_ =
  baseCartEventSchema.extend({
    eventType: z.literal(CustomerEventType.PRODUCT_ADDED_TO_CART),
    storefrontProvider: createEnum(Provider, { enumName: "Provider" })
      .nullish()
      .transform((val) => val ?? Provider.SHOPIFY),
    /** @deprecated This field is shopify-specific. Use `cartLineChanged` instead. */
    cartLine: shopifyCartLineSchema.nullish(),
    /**
     * Raw data from Shopify
     * @deprecated This field is shopify-specific. Use `cartState` instead.
     */
    cart: shopifyPixelEventCartSchema.nullish(),
    /** Inferred data from us (sometimes cart is null but we can still infer total) */
    /** @deprecated This field is shopify-specific. Use `cartState` instead. */
    cartTotal: z
      .object({
        subtotal: z.object({
          amount: z.number(),
          currencyCode: z.string().nullish(),
        }),
      })
      .nullish(),
    /**
     * `cartState.subtotal` converted to shop currency at ingest. Unset when the
     * currency was unknown or the conversion failed.
     */
    subtotalShopCurrency: MoneySchema.nullish(),
  });
export const ProductAddedToCartShoppingEventSchema: z.ZodType<ProductAddedToCartShoppingEvent> =
  ProductAddedToCartShoppingEventSchema_;
export type ProductAddedToCartShoppingEvent = z.infer<
  typeof ProductAddedToCartShoppingEventSchema_
>;

export function standardizeProductAddedToCartShoppingEvent(
  event: ProductAddedToCartShoppingEvent,
  redoCoverageProductIds?: ReadonlySet<string>,
): ProductAddedToCartShoppingEvent {
  if ("cart" in event) {
    const { cartLine, cart, ...rest } = event;
    return {
      ...rest,
      rawEvent: event.rawEvent ?? { cartLine, cart },
      cartLineChanged: shopifyCartLineToRedoCartLine(
        cartLine,
        redoCoverageProductIds,
      ),
      cartState: {
        cartId: cart?.id ?? null,
        lineItems:
          cart?.lines?.map((line) =>
            shopifyCartLineToRedoCartLine(line, redoCoverageProductIds),
          ) ?? null,
        subtotal: {
          amount:
            event.cartState?.subtotal?.amount ??
            cart?.cost?.totalAmount?.amount ??
            null,
          currencyCode:
            event.cartState?.subtotal?.currencyCode ??
            cart?.cost?.totalAmount?.currencyCode ??
            null,
        },
      },
    };
  }
  return event;
}

//////////////////////////////////////
///// PRODUCT REMOVED FROM CART //////
//////////////////////////////////////
export const ProductRemovedFromCartShoppingEventSchema_ =
  baseCartEventSchema.extend({
    eventType: z.literal(CustomerEventType.PRODUCT_REMOVED_FROM_CART),
    /** @deprecated This field is shopify-specific. Use `cartLineChanged` instead. */
    cartLine: shopifyCartLineSchema.nullish(),
    /** @deprecated This field is shopify-specific. Use `cartState` instead. */
    cart: shopifyPixelEventCartSchema.nullish(),
  });
export const ProductRemovedFromCartShoppingEventSchema: z.ZodType<ProductRemovedFromCartShoppingEvent> =
  ProductRemovedFromCartShoppingEventSchema_;
export type ProductRemovedFromCartShoppingEvent = z.infer<
  typeof ProductRemovedFromCartShoppingEventSchema_
>;

export function standardizeProductRemovedFromCartShoppingEvent(
  event: ProductRemovedFromCartShoppingEvent,
  redoCoverageProductIds?: ReadonlySet<string>,
): ProductRemovedFromCartShoppingEvent {
  if ("cart" in event) {
    const { cartLine, cart, ...rest } = event;
    return {
      ...rest,
      rawEvent: event.rawEvent ?? { cartLine, cart },
      cartLineChanged: shopifyCartLineToRedoCartLine(
        cartLine,
        redoCoverageProductIds,
      ),
      cartState: {
        cartId: cart?.id ?? null,
        lineItems:
          cart?.lines?.map((line) =>
            shopifyCartLineToRedoCartLine(line, redoCoverageProductIds),
          ) ?? null,
        subtotal: {
          amount:
            event.cartState?.subtotal?.amount ??
            cart?.cost?.totalAmount?.amount ??
            null,
          currencyCode:
            event.cartState?.subtotal?.currencyCode ??
            cart?.cost?.totalAmount?.currencyCode ??
            null,
        },
      },
    };
  }
  return event;
}

////////////////////////////////////////////
///// CHECKOUT CONTACT INFO SUBMITTED //////
////////////////////////////////////////////

export const CheckoutContactInfoSubmittedShoppingEventSchema_ =
  baseShoppingEventSchema.extend({
    eventType: z.literal(CustomerEventType.CHECKOUT_CONTACT_INFO_SUBMITTED),
    /** @deprecated This field is Shopify pixel-specific. Use `checkoutInfo` instead. */
    checkout: shopifyCheckoutSchema.nullish(),
    checkoutInfo: zExt.coerceNull(redoCheckoutInfoSchema),
  });
export const CheckoutContactInfoSubmittedShoppingEventSchema: z.ZodType<CheckoutContactInfoSubmittedShoppingEvent> =
  CheckoutContactInfoSubmittedShoppingEventSchema_;
export interface CheckoutContactInfoSubmittedShoppingEvent extends z.infer<
  typeof CheckoutContactInfoSubmittedShoppingEventSchema_
> {}

/////////////////////////////
///// CHECKOUT STARTED //////
/////////////////////////////

export const baseCheckoutStartedShoppingEventSchema =
  baseShoppingEventSchema.extend({
    eventType: z.literal(CustomerEventType.CHECKOUT_STARTED),
    checkoutInfo: zExt.coerceNull(redoCheckoutInfoSchema),
    sourceName: z.string().nullish(),
  });
const shopifyCheckoutStartedShoppingEventSchema =
  baseCheckoutStartedShoppingEventSchema.extend({
    storefrontProvider: z.literal(Provider.SHOPIFY),
    /** @deprecated This field is shopify-specific. Use `checkoutInfo` instead. */
    checkout: shopifyCheckoutSchema.nullish(),
    /** @deprecated This field is shopify-specific. Use `cartState` instead. */
    cart: shopifyPixelEventCartSchema.nullish(),
    /** True when the event originated from a Shopify webhook rather than a pixel event. */
    isWebhookCheckout: z.boolean().nullish(),
    gateway: z.string().nullish(),
  });
const commentsoldCheckoutStartedShoppingEvent =
  baseCheckoutStartedShoppingEventSchema.extend({
    storefrontProvider: z.literal(Provider.COMMENTSOLD),
  });
const bigcommerceCheckoutStartedShoppingEvent =
  baseCheckoutStartedShoppingEventSchema.extend({
    storefrontProvider: z.literal(Provider.BIGCOMMERCE),
  });
export const CheckoutStartedShoppingEventSchema_ = z.discriminatedUnion(
  "storefrontProvider",
  [
    shopifyCheckoutStartedShoppingEventSchema,
    commentsoldCheckoutStartedShoppingEvent,
    bigcommerceCheckoutStartedShoppingEvent,
  ],
);
export const CheckoutStartedShoppingEventSchema: z.ZodType<CheckoutStartedShoppingEvent> =
  CheckoutStartedShoppingEventSchema_;
export type CheckoutStartedShoppingEvent = z.infer<
  typeof CheckoutStartedShoppingEventSchema_
>;

function parseCheckoutStartedCheckoutInfo(
  event:
    | CheckoutStartedShoppingEvent
    | CheckoutContactInfoSubmittedShoppingEvent,
  redoCoverageProductIds?: ReadonlySet<string>,
) {
  if ("cart" in event) {
    return (
      event.checkoutInfo ??
      (event.checkout
        ? shopifyCheckoutToRedoCheckout({
            checkout: event.checkout,
            cartId: event.cart?.id ?? null,
            redoCoverageProductIds,
          })
        : null)
    );
  }
  if (event.eventType === CustomerEventType.CHECKOUT_CONTACT_INFO_SUBMITTED) {
    return event.checkout
      ? shopifyCheckoutToRedoCheckout({
          checkout: event.checkout,
          cartId: null,
          redoCoverageProductIds,
        })
      : null;
  }
  return event.checkoutInfo;
}

export function standardizeCheckoutStartedShoppingEvent(
  event:
    | CheckoutStartedShoppingEvent
    | CheckoutContactInfoSubmittedShoppingEvent,
  redoCoverageProductIds?: ReadonlySet<string>,
): CheckoutStartedShoppingEvent {
  const checkoutInfo = parseCheckoutStartedCheckoutInfo(
    event,
    redoCoverageProductIds,
  );
  if ("cart" in event) {
    const { checkout, cart, ...rest } = event;
    return {
      ...rest,
      rawEvent: event.rawEvent ?? { checkout, cart },
      checkoutInfo,
    };
  }
  if (event.eventType === CustomerEventType.CHECKOUT_CONTACT_INFO_SUBMITTED) {
    const { checkout, ...rest } = event;
    return {
      ...rest,
      eventType: CustomerEventType.CHECKOUT_STARTED,
      storefrontProvider: Provider.SHOPIFY,
      rawEvent: { checkout, cart: null },
      checkoutInfo,
    };
  }
  return event;
}

//////////////////////////////////
///// CHECKOUT COMPLETED ////////
//////////////////////////////////

const baseCheckoutCompletedShoppingEventSchema = baseShoppingEventSchema.extend(
  {
    eventType: z.literal(CustomerEventType.CHECKOUT_COMPLETED),
    checkoutInfo: zExt.coerceNull(redoCheckoutInfoSchema),
  },
);
const shopifyCheckoutCompletedShoppingEventSchema =
  baseCheckoutCompletedShoppingEventSchema.extend({
    storefrontProvider: z.literal(Provider.SHOPIFY),
    checkout: shopifyCheckoutSchema.nullish(),
  });
const commentsoldCheckoutCompletedShoppingEventSchema =
  baseCheckoutCompletedShoppingEventSchema.extend({
    storefrontProvider: z.literal(Provider.COMMENTSOLD),
    shopId: z.string().nullish(),
    externalOrderId: z.string().nullish(),
  });
export const CheckoutCompletedShoppingEventSchema_ = z.discriminatedUnion(
  "storefrontProvider",
  [
    shopifyCheckoutCompletedShoppingEventSchema,
    commentsoldCheckoutCompletedShoppingEventSchema,
  ],
);
export type CheckoutCompletedShoppingEvent = z.infer<
  typeof CheckoutCompletedShoppingEventSchema_
>;

export function standardizeCheckoutCompletedShoppingEvent(
  event: CheckoutCompletedShoppingEvent,
  redoCoverageProductIds?: ReadonlySet<string>,
): CheckoutCompletedShoppingEvent {
  if ("checkout" in event) {
    const { checkout, ...rest } = event;
    return {
      ...rest,
      rawEvent: event.rawEvent ?? { checkout },
      checkoutInfo:
        event.checkoutInfo ??
        (checkout
          ? shopifyCheckoutToRedoCheckout({
              checkout,
              cartId: null,
              redoCoverageProductIds,
            })
          : null),
    };
  }
  return event;
}

//////////////////////////////////
///// PAYMENT FAILED ////////////
//////////////////////////////////

export const PaymentFailedShoppingEventSchema_ = baseShoppingEventSchema.extend(
  {
    eventType: z.literal(CustomerEventType.PAYMENT_FAILED),
    paymentFailureStage: createEnum(PaymentFailureStage, {
      enumName: "PaymentFailureStage",
    }),
    externalOrderId: z.string().nullish(),
  },
);
export const PaymentFailedShoppingEventSchema: z.ZodType<PaymentFailedShoppingEvent> =
  PaymentFailedShoppingEventSchema_;
export interface PaymentFailedShoppingEvent extends z.infer<
  typeof PaymentFailedShoppingEventSchema_
> {}

//////////////////////////////////
///// CHECKOUT INFO CHANGED //////
//////////////////////////////////

// is a shopping event, but does not need externalEventId
export const CheckoutInfoChangedShoppingEventSchema_ =
  baseCustomerEventSchema.extend({
    eventType: z.literal(CustomerEventType.CHECKOUT_INFO_CHANGED),
    checkoutToken: z.string(),
    subtotalPrice: MoneySchema,
    itemQuantity: z.number(),
    customerOrderCount: z.union([
      z.object({
        status: z.literal(CheckoutConversionMetricInformationStatus.KNOWN),
        value: z.number(),
      }),
      z.object({
        status: z.literal(CheckoutConversionMetricInformationStatus.UNKNOWN),
      }),
    ]),
    treatmentId: z.union([
      z.object({
        status: z.literal(CheckoutConversionMetricInformationStatus.KNOWN),
        value: z.string().optional(),
      }),
      z.object({
        status: z.literal(CheckoutConversionMetricInformationStatus.UNKNOWN),
      }),
    ]),
    localDateTime: z.object({
      isoDateTime: z.date(),
      timeZone: z.string(),
      localDayOfWeek: createEnum(LocalDayOfWeek, {
        enumName: "LocalDayOfWeek",
      }),
      localTimeInSecondsSinceMidnight: z.number(),
    }),
    location: z
      .object({
        countryCode: z.string().optional(),
        provinceCode: z.string().optional(),
      })
      .optional(),
    metricSource: createEnum(CheckoutConversionMetricSource, {
      enumName: "CheckoutConversionMetricSource",
    }).optional(),
  });
export const CheckoutInfoChangedShoppingEventSchema: z.ZodType<CheckoutInfoChangedShoppingEvent> =
  CheckoutInfoChangedShoppingEventSchema_;
export interface CheckoutInfoChangedShoppingEvent extends z.infer<
  typeof CheckoutInfoChangedShoppingEventSchema_
> {}

////////////////////////////////////////////////////
///// CHECKOUT PROBABILITY CALCULATED //////////////
////////////////////////////////////////////////////

// is a shopping event, but does not need externalEventId
export const CheckoutProbabilityCalculatedShoppingEventSchema_ =
  baseCustomerEventSchema.extend({
    eventType: z.literal(CustomerEventType.CHECKOUT_PROBABILITY_CALCULATED),
    sessionId: z.string(),
    instantaneousHazardProbability: z.number(),
    cumulativeConversionProbability: z.number(),
    sessionAge: z.number(),
    totalBuckets: z.number(),
  });
export const CheckoutProbabilityCalculatedShoppingEventSchema: z.ZodType<CheckoutProbabilityCalculatedShoppingEvent> =
  CheckoutProbabilityCalculatedShoppingEventSchema_;
export interface CheckoutProbabilityCalculatedShoppingEvent extends z.infer<
  typeof CheckoutProbabilityCalculatedShoppingEventSchema_
> {}

////////////////////////////////////////////////////
///// PACKAGE PROTECTION PRICE EXPOSED //
////////////////////////////////////////////////////

export const PackageProtectionPriceExposedShoppingEventSchema_ =
  baseCustomerEventSchema.extend({
    eventType: z.literal(CustomerEventType.PACKAGE_PROTECTION_PRICE_EXPOSED),
    anonymousId: z.string(),
    treatmentId: z.string().optional(),
    isStagecoachArm: z.boolean(),
    modelVersionId: z.string().optional(),
    betaSampleIndex: z.number().optional(),
    packageProtectionPriceDollars: z.number().optional(),
    returnsCoveragePriceDollars: z.number().optional(),
    cartValue: z.number(),
    cartItemCount: z.number(),
    cartHash: z.string(),
    checkoutProbability: z.number().optional(),
  });
export const PackageProtectionPriceExposedShoppingEventSchema: z.ZodType<PackageProtectionPriceExposedShoppingEvent> =
  PackageProtectionPriceExposedShoppingEventSchema_;
export interface PackageProtectionPriceExposedShoppingEvent extends z.infer<
  typeof PackageProtectionPriceExposedShoppingEventSchema_
> {}

export type PixelEvent =
  | ProductViewedShoppingEvent
  | ProductAddedToCartShoppingEvent
  | ProductRemovedFromCartShoppingEvent
  | CheckoutContactInfoSubmittedShoppingEvent
  | CheckoutStartedShoppingEvent
  | CheckoutCompletedShoppingEvent
  | PaymentFailedShoppingEvent
  | StorePageViewedShoppingEvent
  | CollectionViewedShoppingEvent
  | CheckoutInfoChangedShoppingEvent
  | CheckoutProbabilityCalculatedShoppingEvent
  | PackageProtectionPriceExposedShoppingEvent;
